import { nyDateInput, fmtNyTime } from "./prepDates";

// FullCalendar event colors by category. Release vs due is conveyed by the ▶/⏹
// prefix in the title, not by color.
export const PREP_CAL_COLORS = {
    exam: "#1976d2",      // blue
    homework: "#2e7d32",  // green
    done: "#6a1b9a",      // purple — things the student actually completed
};

// One all-day event anchored to the NY calendar day of a stored UTC timestamp,
// tagged with its category (so the calendar can toggle exam/homework on/off) and
// labelled with the NY time. (All-day avoids FullCalendar re-bucketing the instant
// into the browser's zone.)
const ev = (title, iso, color, category) => {
    const day = nyDateInput(iso); // "" when iso is null/empty
    if (!day) return null;
    const time = fmtNyTime(iso);
    return {
        title: time ? `${title} · ${time}` : title,
        start: day,
        allDay: true,
        backgroundColor: color,
        borderColor: color,
        extendedProps: { category },
    };
};

// id -> display label for SUBJECT program-courses. Accepts either the editor's
// subjectCourses rows ({ programCourseId, label, course }) or the detail courses
// ({ id, label, course }).
export const subjectLabelMap = (courses = []) => {
    const m = {};
    courses.forEach((c) => {
        const id = c.programCourseId ?? c.id;
        if (id != null) m[id] = c.label || c.course?.course_title || "Homework";
    });
    return m;
};

// Program-level schedule: exam + homework-round release/due markers. Set
// includeHwDue=false to let a per-student overlay own the homework-due markers.
export const programScheduleEvents = ({ exams = [], rounds = [], subjectLabels = {}, includeHwDue = true } = {}) => {
    const out = [];
    exams.forEach((e) => {
        out.push(ev(`▶ ${e.exam_title}`, e.release_date, PREP_CAL_COLORS.exam, "exam"));
        out.push(ev(`⏹ ${e.exam_title} (due)`, e.due_date, PREP_CAL_COLORS.exam, "exam"));
    });
    rounds.forEach((r) => {
        const subj = subjectLabels[r.prep_program_course_id] || "Homework";
        out.push(ev(`▶ ${subj} HW`, r.release_date, PREP_CAL_COLORS.homework, "homework"));
        if (includeHwDue) out.push(ev(`⏹ ${subj} HW (due)`, r.due_date, PREP_CAL_COLORS.homework, "homework"));
    });
    return out.filter(Boolean);
};

// Per-student overlay: practice tests actually taken (on finished_at) + this
// student's homework due, grouped by round, annotated with mastery progress.
export const studentOverlayEvents = ({ scores = [], assignments = [], subjectLabels = {} } = {}) => {
    const out = [];
    scores.forEach((s) => out.push(ev(`✔ ${s.test_title}`, s.finished_at, PREP_CAL_COLORS.done, "done")));

    const byRound = {};
    assignments.forEach((a) => {
        const key = a.round_id ?? a.prep_homework_round_id;
        if (key == null || !a.round_due_date) return;
        if (!byRound[key]) byRound[key] = { due: a.round_due_date, pc: a.prep_program_course_id, total: 0, mastered: 0 };
        byRound[key].total += 1;
        if (a.mastered) byRound[key].mastered += 1;
    });
    Object.values(byRound).forEach((g) => {
        const subj = subjectLabels[g.pc] || "Homework";
        out.push(ev(`⏹ ${subj} HW · ${g.mastered}/${g.total} mastered`, g.due, PREP_CAL_COLORS.homework, "homework"));
    });
    return out.filter(Boolean);
};

// Earliest event date ("yyyy-MM-dd"), to focus the calendar on the schedule's start.
export const earliestEventDate = (events = []) =>
    events.map((e) => e.start).filter(Boolean).sort()[0];
